import { Stack } from '@mui/material';
import {
    BooleanInput,
    ReferenceInput,
    SelectInput,
    TextInput,
    required,
    useRecordContext,
} from 'react-admin';
import ImageEditorField from '@app/misc/ImageEditorField';
import { AnimationInput } from '@domain/animation/animation';
import { MissionTypeLightCard } from '@app/mission_type/MissionTypeLightCard';
import { UrlInput } from '@app/misc/UrlInput';
import { useResource } from '@app/misc/useResource';
import { showModeReadOnlySx } from '@app/root/theme';

export const AnimationInputs = ({
    readOnly = false,
}: {
    readOnly?: boolean;
}) => {
    const record = useRecordContext<AnimationInput>();
    const { data: imageSrc } = useResource(record?.imageId);

    return (
        <Stack gap={2} p={1} sx={readOnly ? showModeReadOnlySx : undefined}>
            <TextInput
                source="name"
                helperText={false}
                validate={required()}
                readOnly={readOnly}
            />
            <ImageEditorField
                source="imageInput"
                height={200}
                emptyText={record?.name ?? ''}
                linkPosition="bottom"
                src={imageSrc ?? undefined}
                readOnly={readOnly}
            />
            <TextInput
                source="theme"
                helperText="e.g. Substainable Nutrition, Ocean"
                readOnly={readOnly}
            />
            <TextInput
                source="duration"
                helperText="e.g. 10 to 15 minutes, 30 minutes, 1 hour"
                readOnly={readOnly}
            />
            <TextInput
                source="ageTarget"
                helperText="e.g. 6 to 12 years, 3+, all ages"
                readOnly={readOnly}
            />
            <ReferenceInput
                source="missionType"
                reference="missionType"
                sort={{ field: 'name', order: 'ASC' }}
                readOnly={readOnly}
            >
                <SelectInput
                    source="missionType"
                    label="Mission type"
                    optionText={<MissionTypeLightCard />}
                    helperText={false}
                    validate={required()}
                    readOnly={readOnly}
                />
            </ReferenceInput>
            <TextInput
                source="description"
                helperText={false}
                multiline
                readOnly={readOnly}
            />
            <UrlInput
                source="documentLink"
                label="Documentation link"
                helperText="e.g. Link to the documentation or external website"
                readOnly={readOnly}
            />
            <BooleanInput
                source="noSpecificMaterialRequired"
                label="No specific material required"
                defaultValue={false}
                helperText="When enabled, this animation is understood as not needing dedicated physical material at the antenna and so always available for selection in missions."
                readOnly={readOnly}
            />
            <BooleanInput
                source="enabled"
                label="Enabled"
                defaultValue={true}
                helperText="If disabled, this animation will not be available for selection in missions"
                readOnly={readOnly}
            />
        </Stack>
    );
};
